import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../ui/Icon';
import Reveal from '../ui/Reveal';
import PatternAccent from '../ui/PatternAccent';
import plant from '../../assets/home/plant.webp';
import logoMark from '../../assests/ampera_logo.png';

// Branches and the culture each get a full-width row rather than sharing one.
// Side by side, the branch rail was squeezed into a column too narrow to show
// more than two cards, and the culture panel could only fit half of SANTRI.

// All six principles, same source as the careers page — a visitor should see
// the whole of what the acronym means, not the three that happened to fit.
const VALUES = [
  { key: 'sopan', icon: 'handHeart' },
  { key: 'amanah', icon: 'scale' },
  { key: 'nyaman', icon: 'home' },
  { key: 'tawadhu', icon: 'humility' },
  { key: 'rajin', icon: 'sparkle' },
  { key: 'istiqomah', icon: 'repeat' },
];

// Every branch is called "Warung Nasi Ampera <somewhere>", so on a narrow
// card the shared prefix is all you can read and every branch looks
// identical. Show the part that actually distinguishes them.
const shortName = (name = '') =>
  name.replace(/^\s*(warung\s+nasi\s+)?ampera\s*/i, '').trim() || name;

// open_hours is an empty object on every row today. Rendering "Open daily"
// off that would be asserting opening times we do not have.
const hoursLabel = (h) => {
  if (!h) return null;
  if (typeof h === 'string') return h.trim() || null;
  return null;
};

export default function BranchesAndValues({ branches, loading }) {
  const { t } = useTranslation();

  return (
    <>
      {/* ---------- branches ---------- */}
      <section className="mx-auto max-w-7xl px-4 pb-14 sm:px-6 md:pb-20 lg:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl border border-gray-100 bg-[#fdf8f4] px-6 py-8 shadow-sm md:px-10 md:py-10">
            <img
              src={plant}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute -bottom-8 -left-10 z-0 w-40 opacity-60 md:w-56"
            />

            <div className="relative z-10">
              <div className="mb-6 text-center">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
                  {t('home.branches.eyebrow', 'Find Us Near You')}
                </span>
                <h2 className="mt-2 font-serif text-2xl font-bold text-gray-900 md:text-3xl">
                  {t('home.branches.title', 'Our Branches')}
                </h2>
                <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-gray-600">
                  {t('home.branches.body')}
                </p>
              </div>

              {/* Horizontal scroll rather than a carousel: no JS, nothing
                  hidden from search, and a thumb-swipe is the native gesture
                  on the phones most of this traffic comes from. Centred while
                  the cards still fit, scrollable once they do not. */}
              {/* justify-center on a scroll container makes the leading
                  overflow unreachable — the first card gets clipped and the
                  rail opens mid-scroll. Centre the track with mx-auto
                  instead: it centres when the cards fit and does nothing
                  when they do not. */}
              <div className="-mx-2 flex overflow-x-auto px-2 pb-3">
                <div className="mx-auto flex w-max snap-x snap-mandatory gap-4">
                {loading
                  ? [0, 1, 2, 3].map((i) => (
                    <div key={i} className="h-56 w-52 shrink-0 animate-pulse rounded-xl bg-gray-100" />
                  ))
                  : branches.map((b, i) => {
                    const hours = hoursLabel(b.open_hours);
                    return (
                      <Reveal key={b.id} delay={Math.min(i, 5) * 0.08} from="scale" className="shrink-0">
                        <Link
                          to={`/branches/${b.slug || b.id}`}
                          className="group flex w-52 snap-start flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
                        >
                          <div className="flex h-28 items-center justify-center overflow-hidden bg-primary-50/60">
                            {b.featured_image_url
                              ? (
                                <img
                                  src={b.featured_image_url}
                                  alt=""
                                  loading="lazy"
                                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                              )
                              : <img src={logoMark} alt="" aria-hidden="true" className="h-10 w-10 opacity-25" />}
                          </div>
                          <div className="flex flex-1 flex-col p-3.5">
                            <div className="truncate text-sm font-semibold text-gray-900" title={b.name}>
                              {shortName(b.name)}
                            </div>
                            {b.city && <div className="mt-0.5 truncate text-xs text-gray-500">{b.city}</div>}
                            {b.address && (
                              <div className="mt-1 line-clamp-2 text-xs leading-snug text-gray-400">{b.address}</div>
                            )}
                            {hours && (
                              <div className="mt-1.5 flex items-center gap-1 text-xs font-medium text-green-600">
                                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                                {hours}
                              </div>
                            )}
                          </div>
                        </Link>
                      </Reveal>
                    );
                  })}
                </div>
              </div>

              <div className="mt-5 text-center">
                <Link
                  to="/branches"
                  className="inline-flex items-center gap-2 rounded-lg border border-primary-300 bg-white px-6 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50"
                >
                  <Icon name="mapPin" className="h-4 w-4" />
                  {t('home.branches.cta', 'Find Nearest Branch')}
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ---------- rooted in values ---------- */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 md:pb-24 lg:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white px-6 py-8 shadow-sm md:px-10 md:py-10">
            <PatternAccent at="bottom-right" className="w-56" />
            <PatternAccent at="top-left" className="w-36" opacity={0.07} flip />

            <div className="relative z-10">
              <div className="mx-auto max-w-2xl text-center">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
                  {t('home.values.eyebrow', 'Our Culture')}
                </span>
                <h2 className="mt-2 font-serif text-2xl font-bold leading-snug text-gray-900 md:text-3xl">
                  {t('home.values.title', 'Rooted in Values, Guided by Tradition')}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-gray-600">
                  {t('home.values.body')}
                </p>
              </div>

              <ul className="mt-8 grid gap-x-8 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
                {VALUES.map((v, i) => (
                  <Reveal key={v.key} delay={i * 0.06} as="li" className="flex gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                      <Icon name={v.icon} className="h-5 w-5" />
                    </span>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {t(`careers.santri.${v.key}.title`)}
                      </div>
                      <p className="mt-0.5 text-sm leading-snug text-gray-600">
                        {t(`careers.santri.${v.key}.body`)}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
